import { useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, deleteDoc, doc, query, where, getDocs } from 'firebase/firestore';
import { storage, db } from '../lib/firebase';
export interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  category: string;
  storageUrl: string;
  storagePath: string;
  uploadedAt: Date;
}
export interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
}
export function useFileUpload(userId: string) {
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({});
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const uploadFile = async (file: File, category: string): Promise<void> => {
    console.log('🚀 Starting upload for:', file.name, 'Category:', category);
    const fileName = `${Date.now()}_${file.name}`;
    const storagePath = `users/${userId}/documents/${category}/${fileName}`;
    const storageRef = ref(storage, storagePath);
    console.log('📁 Storage path:', storagePath);

    // Initialize progress
    setUploadProgress(prev => ({
      ...prev,
      [file.name]: {
        fileName: file.name,
        progress: 0,
        status: 'uploading'
      }
    }));
    try {
      const uploadTask = uploadBytesResumable(storageRef, file);
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed', snapshot => {
          const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
          console.log('📊 Upload progress:', progress.toFixed(2) + '%', 'for', file.name);
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: {
              fileName: file.name,
              progress,
              status: 'uploading'
            }
          }));
        }, error => {
          console.error('❌ Upload error:', error);
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: {
              fileName: file.name,
              progress: 0,
              status: 'error',
              error: error.message
            }
          }));
          reject(error);
        }, async () => {
          try {
            console.log('✅ Upload complete, getting download URL...');
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('🔗 Download URL:', downloadURL);

            // Save metadata to Firestore
            console.log('💾 Saving to Firestore...');
            const docRef = await addDoc(collection(db, 'user_documents'), {
              userId,
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              category,
              storageUrl: downloadURL,
              storagePath,
              uploadedAt: new Date()
            });
            console.log('✅ Firestore document created:', docRef.id);
            const newFile: UploadedFile = {
              id: docRef.id,
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              category,
              storageUrl: downloadURL,
              storagePath,
              uploadedAt: new Date()
            };
            setUploadedFiles(prev => {
              console.log('📝 Adding file to uploadedFiles array');
              return [...prev, newFile];
            });
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: {
                fileName: file.name,
                progress: 100,
                status: 'complete'
              }
            }));
            console.log('🎉 Upload fully complete for:', file.name);

            // Clear progress after 2 seconds
            setTimeout(() => {
              setUploadProgress(prev => {
                const newProgress = {
                  ...prev
                };
                delete newProgress[file.name];
                console.log('🧹 Cleared progress for:', file.name);
                return newProgress;
              });
            }, 2000);
            resolve();
          } catch (error) {
            console.error('❌ Firestore save error:', error);
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('❌ Upload initialization error:', error);
      throw error;
    }
  };
  const deleteFile = async (file: UploadedFile): Promise<void> => {
    try {
      // Delete from Storage
      const storageRef = ref(storage, file.storagePath);
      await deleteObject(storageRef);

      // Delete from Firestore
      await deleteDoc(doc(db, 'user_documents', file.id));

      // Update local state
      setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  };
  const loadUserFiles = async (category?: string): Promise<void> => {
    try {
      const q = category ? query(collection(db, 'user_documents'), where('userId', '==', userId), where('category', '==', category)) : query(collection(db, 'user_documents'), where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      const files: UploadedFile[] = [];
      querySnapshot.forEach(doc => {
        const data = doc.data();
        files.push({
          id: doc.id,
          fileName: data.fileName,
          fileSize: data.fileSize,
          fileType: data.fileType,
          category: data.category,
          storageUrl: data.storageUrl,
          storagePath: data.storagePath,
          uploadedAt: data.uploadedAt?.toDate() || new Date()
        });
      });
      setUploadedFiles(files);
    } catch (error) {
      console.error('Load files error:', error);
      throw error;
    }
  };
  return {
    uploadFile,
    deleteFile,
    loadUserFiles,
    uploadProgress,
    uploadedFiles
  };
}